import { useCallback, useEffect, useState } from 'react';
import { useAIAuth } from '../lib/AIAuthContext';
import { deleteSession, fetchSessions } from '../lib/sessionsApi';
import type { ChatErrorCode, Session } from '../types/types.ts';
import { useChatStore } from './useChatStore';

// Lädt die gespeicherten Chats des Users und löscht einzelne
export function useSessions() {
  const { getAccessToken } = useAIAuth();
  const [sessions, setSessions] = useState<Session[]>();
  const [error, setError] = useState<ChatErrorCode>();

  useEffect(() => {
    const controller = new AbortController();
    void fetchSessions({ getAccessToken, signal: controller.signal }).then(
      (result) => {
        if (controller.signal.aborted) {
          return;
        }
        if ('error' in result) {
          setError(result.error);
        } else {
          setSessions(result.data);
        }
      },
    );
    return () => controller.abort();
  }, [getAccessToken]);

  const forget = useCallback((sessionId: string) => {
    setSessions((current) =>
      current?.filter((session) => session.id !== sessionId),
    );
  }, []);

  const remove = useCallback(
    async (sessionId: string): Promise<ChatErrorCode | undefined> => {
      const result = await deleteSession(sessionId, { getAccessToken });
      // 404: Der Chat ist schon weg oder nicht verfügbar
      if ('error' in result && result.error !== 'SESSION_NOT_FOUND') {
        return result.error;
      }
      forget(sessionId);
      const { sessionId: activeSessionId, reset } = useChatStore.getState();
      if (activeSessionId === sessionId) {
        reset();
      }
      return undefined;
    },
    [getAccessToken, forget],
  );

  return {
    sessions,
    error,
    isLoading: sessions === undefined && error === undefined,
    forget,
    remove,
  };
}
